import { useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Search, UserPlus } from "lucide-react";
import type { GroupDto, UserDto } from "@platform/core";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataGrid,
  Dialog,
  EmptyState,
  Field,
  Input,
  Skeleton,
  Spinner,
  Textarea,
  useToast,
  type GridColDef,
} from "@platform/ui";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { UserTypeBadge, accessSummary } from "../../lib/access";
import { CopyButton } from "../../lib/copy";
import { errorText } from "../../lib/errors";
import { formatDate } from "../../lib/format";
import { useGridText } from "../../lib/grid";
import { ConfirmDialog } from "../../layout/ConfirmDialog";
import { ErrorBanner, PageCard, ReloadButton, Section } from "../../layout/Page";
import { Avatar } from "../users/UsersPage";

function GroupForm({ group }: { group: GroupDto }) {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? "");
  const save = useMutation({
    mutationFn: () =>
      api.groups.update(group.id, { name: name.trim(), description: description.trim() || null }),
    onSuccess: (g) => {
      qc.setQueryData(["group", group.id], g);
      void qc.invalidateQueries({ queryKey: ["groups"] });
      toast({ title: t("common.saved"), tone: "success" });
    },
  });
  const editable = can("groups:write");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <fieldset disabled={!editable}>
        {save.isError && (
          <Alert tone="error" className="mb-4">
            {errorText(save.error)}
          </Alert>
        )}
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Field label={t("groups.name")} required>
            {(p) => <Input {...p} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t("groups.description")}>
            {(p) => (
              <Textarea
                {...p}
                className="min-h-9"
                rows={1}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
        </div>
      </fieldset>
      {editable && (
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
            {t("common.save")}
          </Button>
        </div>
      )}
    </form>
  );
}

function AddMembersDialog({
  group,
  memberIds,
  open,
  onOpenChange,
}: {
  group: GroupDto;
  memberIds: ReadonlySet<string>;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  const api = useApi();
  const toast = useToast();
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Map<string, UserDto>>(new Map());
  const results = useQuery({
    queryKey: ["users", "member-search", q],
    queryFn: () => api.users.list({ ...(q ? { q } : {}), limit: 20 }),
    enabled: open,
    placeholderData: keepPreviousData,
  });
  const reset = () => {
    setInput("");
    setQ("");
    setPicked(new Map());
  };
  const add = useMutation({
    mutationFn: () => api.groups.addMembers(group.id, [...picked.keys()]),
    onSuccess: (r) => {
      toast({ title: t("groups.membersAdded", { count: r.added.length }), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["group", group.id] });
      void qc.invalidateQueries({ queryKey: ["groups"] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      reset();
      onOpenChange(false);
    },
  });
  const toggle = (u: UserDto, on: boolean) => {
    const next = new Map(picked);
    if (on) next.set(u.id, u);
    else next.delete(u.id);
    setPicked(next);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      size="lg"
      title={t("groups.addMembersTitle", { name: group.name })}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="confirm-add-members"
            loading={add.isPending}
            disabled={picked.size === 0}
            onClick={() => add.mutate()}
          >
            {t("groups.addSelected", { count: picked.size })}
          </Button>
        </>
      }
    >
      {add.isError && (
        <Alert tone="error" className="mb-4">
          {errorText(add.error)}
        </Alert>
      )}
      <form
        role="search"
        className="mb-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(input.trim());
        }}
      >
        <Input
          type="search"
          aria-label={t("users.searchPlaceholder")}
          placeholder={t("users.searchPlaceholder")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <Button type="submit" variant="secondary">
          <Search className="size-4" aria-hidden />
          {t("common.search")}
        </Button>
      </form>
      {picked.size > 0 && (
        <div className="mb-3 flex flex-wrap gap-1" aria-live="polite">
          {[...picked.values()].map((u) => (
            <Badge key={u.id} tone="info">
              {u.email}
            </Badge>
          ))}
        </div>
      )}
      <fieldset className="max-h-80 overflow-y-auto rounded-[var(--radius-control)] border border-line">
        <legend className="sr-only">{t("groups.searchResults")}</legend>
        {results.isError ? (
          <ErrorBanner error={results.error} onRetry={() => void results.refetch()} />
        ) : results.isPending ? (
          <div className="flex justify-center p-6">
            <Spinner label={t("common.loading")} />
          </div>
        ) : results.data.items.length === 0 ? (
          <p className="p-4 text-sm text-ink-2">{t("users.emptyFiltered")}</p>
        ) : (
          <ul className="divide-y divide-line">
            {results.data.items.map((u) => {
              const member = memberIds.has(u.id);
              return (
                <li key={u.id} className="px-3 py-2">
                  <Checkbox
                    data-testid="member-candidate"
                    disabled={member}
                    checked={member || picked.has(u.id)}
                    onChange={(e) => toggle(u, e.target.checked)}
                    label={
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{u.displayName}</span>
                        <span className="text-ink-2">{u.email}</span>
                        <UserTypeBadge type={u.userType} />
                        {member && <Badge tone="success">{t("groups.alreadyMember")}</Badge>}
                      </span>
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </fieldset>
    </Dialog>
  );
}

function MembersSection({ group }: { group: GroupDto }) {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const text = useGridText();
  const members = useQuery({
    queryKey: ["group", group.id, "members"],
    queryFn: () => api.groups.members(group.id),
  });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<UserDto | null>(null);
  const remove = useMutation({
    mutationFn: (userId: string) => api.groups.removeMember(group.id, userId),
    onSuccess: () => {
      setRemoving(null);
      toast({ title: t("groups.memberRemoved"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["group", group.id] });
      void qc.invalidateQueries({ queryKey: ["groups"] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const writable = can("groups:write");
  const columns = useMemo<GridColDef<UserDto>[]>(
    () => [
      {
        field: "displayName",
        headerName: t("user.displayName"),
        minWidth: 200,
        hideable: false,
        renderCell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2">
            <Avatar user={row} />
            <span className="truncate font-medium">{row.displayName}</span>
          </span>
        ),
      },
      { field: "email", headerName: t("user.email"), minWidth: 220 },
      {
        field: "userType",
        headerName: t("access.userType"),
        type: "singleSelect",
        width: 140,
        // Group members always have a type (the root account cannot join groups).
        valueOptions: [
          ...new Set((members.data?.items ?? []).flatMap((u) => (u.userType ? [u.userType] : []))),
        ].map((v) => ({
          value: v,
          label: v,
        })),
        renderCell: ({ row }) => <UserTypeBadge type={row.userType} />,
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        width: 120,
        align: "right",
        hideable: false,
        disableExport: true,
        renderCell: ({ row }) =>
          writable ? (
            <Button
              variant="text"
              size="sm"
              className="text-danger"
              aria-label={t("groups.removeMemberLabel", { name: row.displayName })}
              onClick={(e) => {
                e.stopPropagation();
                setRemoving(row);
              }}
            >
              {t("common.remove")}
            </Button>
          ) : null,
      },
    ],
    [t, writable, members.data],
  );
  const memberIds = useMemo(() => new Set((members.data?.items ?? []).map((u) => u.id)), [members.data]);
  return (
    <>
      {members.isError && <ErrorBanner error={members.error} onRetry={() => void members.refetch()} />}
      <DataGrid
        id="group-members"
        label={t("groups.members")}
        localeText={text}
        rows={members.data?.items ?? []}
        columns={columns}
        loading={members.isFetching}
        onRowClick={(u) => void navigate({ to: "/users/$id", params: { id: u.id } })}
        getRowAttributes={(u) => ({ "data-testid": "member-row", "data-email": u.email })}
        noRowsContent={<EmptyState title={t("groups.noMembers")} />}
        toolbarActions={
          <>
            <ReloadButton onClick={() => void members.refetch()} />
            {writable && (
              <Button data-testid="add-members-button" onClick={() => setAdding(true)}>
                <UserPlus className="size-4" aria-hidden />
                {t("groups.addMembers")}
              </Button>
            )}
          </>
        }
      />
      <AddMembersDialog group={group} memberIds={memberIds} open={adding} onOpenChange={setAdding} />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={t("groups.removeMemberTitle")}
        confirmLabel={t("common.remove")}
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      >
        <p className="text-sm">
          {t("groups.removeMemberBody", { name: removing?.displayName ?? "", group: group.name })}
        </p>
        {remove.isError && (
          <Alert tone="error" className="mt-3">
            {errorText(remove.error)}
          </Alert>
        )}
      </ConfirmDialog>
    </>
  );
}

function ApplicationsSection({ group }: { group: GroupDto }) {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const clients = useQuery({
    queryKey: ["clients"],
    queryFn: api.clients.list,
    enabled: can("clients:read"),
  });
  if (!can("clients:read")) return null;
  const using = (clients.data?.items ?? []).filter(
    (c) => c.access.mode === "restricted" && c.access.groups.includes(group.id),
  );
  return (
    <Section title={t("groups.applications")}>
      {clients.isError ? (
        <ErrorBanner error={clients.error} onRetry={() => void clients.refetch()} />
      ) : clients.isPending ? (
        <Skeleton className="h-10" />
      ) : using.length === 0 ? (
        <p className="text-sm text-ink-2">{t("groups.noApplications")}</p>
      ) : (
        <ul className="divide-y divide-line" data-testid="group-applications">
          {using.map((c) => (
            <li key={c.clientId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <Link
                to="/oauth-clients/$clientId"
                params={{ clientId: c.clientId }}
                className="font-medium text-primary-dark hover:underline"
              >
                {c.name}
              </Link>
              <code className="font-mono text-xs text-ink-2">{c.clientId}</code>
              <span className="text-xs text-ink-2">{accessSummary(c.access, t)}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export function GroupDetailPage() {
  const groupId = useParams({ strict: false }).groupId ?? "";
  const { t, locale } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const group = useQuery({ queryKey: ["group", groupId], queryFn: () => api.groups.get(groupId) });
  const [deleting, setDeleting] = useState(false);
  const del = useMutation({
    mutationFn: () => api.groups.remove(groupId),
    onSuccess: () => {
      toast({ title: t("groups.deleted"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["groups"] });
      void navigate({ to: "/groups" });
    },
  });
  return (
    <div className="mx-auto flex max-w-[var(--container-content)] flex-col gap-4">
      <Link
        to="/groups"
        className="inline-flex items-center gap-1 self-start text-sm text-primary-dark hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t("groups.back")}
      </Link>
      {group.isError ? (
        <PageCard>
          <ErrorBanner error={group.error} onRetry={() => void group.refetch()} />
        </PageCard>
      ) : group.isPending ? (
        <Skeleton className="h-40" />
      ) : (
        <>
          <PageCard>
            <div className="flex flex-wrap items-center gap-3 border-b border-line px-6 py-5">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-medium break-all">{group.data.name}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-2">
                  <code className="font-mono text-xs">{group.data.id}</code>
                  <CopyButton value={group.data.id} />
                  <Badge tone="info">{t("groups.memberCount", { count: group.data.memberCount })}</Badge>
                  <span>{formatDate(group.data.createdAt, locale, config.timeZone)}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <GroupForm key={`${group.data.name}|${group.data.description ?? ""}`} group={group.data} />
            </div>
          </PageCard>
          <PageCard>
            <Section title={t("groups.members")}>
              <p className="text-sm text-ink-2">{t("groups.membersHint")}</p>
            </Section>
            <MembersSection group={group.data} />
          </PageCard>
          <PageCard>
            <ApplicationsSection group={group.data} />
          </PageCard>
          {can("groups:write") && (
            <PageCard>
              <Section title={<span className="text-danger">{t("user.danger")}</span>}>
                <Button variant="danger" data-testid="delete-group-button" onClick={() => setDeleting(true)}>
                  {t("groups.delete")}
                </Button>
              </Section>
            </PageCard>
          )}
          <ConfirmDialog
            open={deleting}
            onOpenChange={(o) => {
              setDeleting(o);
              if (!o) del.reset();
            }}
            title={t("groups.deleteTitle", { name: group.data.name })}
            confirmLabel={t("common.delete")}
            destructive
            loading={del.isPending}
            onConfirm={() => del.mutate()}
          >
            <p className="text-sm">{t("groups.deleteBody")}</p>
            {del.isError && (
              <Alert tone="error" className="mt-3">
                {errorText(del.error)}
              </Alert>
            )}
          </ConfirmDialog>
        </>
      )}
    </div>
  );
}
